import React, { useState, useEffect, useRef } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import { adminService, HistoricalPosition } from '../services/adminService';
import { accountService, PositionStatistics } from '../services/accountService';
import { useToastStore } from '../stores/useToastStore';
import { PositionSide, ProfitTimeRange } from '@/services/enums';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import FilterBar from '../components/historicalPositions/FilterBar';
import SummaryCard from '../components/historicalPositions/SummaryCard';
import SummaryStatsModule from '../components/historicalPositions/SummaryStatsModule';
import PositionList from '../components/historicalPositions/PositionList';
import PaginationBar from '../components/historicalPositions/PaginationBar';
import PageHeader from '../components/PageHeader';
import { StrategyVersionFilter } from '@/services/enums';

type DirectionFilter = 'all' | PositionSide;
type TypeFilter = StrategyVersionFilter;

const HistoricalPositions: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<HistoricalPosition[]>([]);
  const [summary, setSummary] = useState<{
    total: number;
    totalPnL: number;
    totalRealizedPnl: number;
    longCount: number;
    shortCount: number;
    longPnL: number; // 做多总盈亏
    longRealizedPnl: number; // 做多已实现盈亏
    shortPnL: number; // 做空总盈亏
    shortRealizedPnl: number; // 做空已实现盈亏
    type2Count: number;
    type3Count: number;
    tagStats?: Array<{
      tag: string;
      count: number;
      totalPnL: number;
      totalRealizedPnl: number;
    }>;
  } | null>(null);
  const [pagination, setPagination] = useState<{
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  }>({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });
  const [selectedDirection, setSelectedDirection] = useState<DirectionFilter>('all');
  const [selectedType, setSelectedType] = useState<TypeFilter>('all');
  const [symbolFilter, setSymbolFilter] = useState('');
  const [profitTimeRange, setProfitTimeRange] = useState<ProfitTimeRange>('15d');
  const [stats, setStats] = useState<PositionStatistics | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const setCurrentPage = useSetCurrentPage();
  const showToast = useToastStore((state) => state.show);
  const hasLoadedRef = useRef(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const loadingRef = useRef(false);

  const fetchRecords = async (page: number = pagination.page) => {
    // 防止重复调用
    if (loadingRef.current) {
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);

      const direction = selectedDirection === 'all' ? undefined : selectedDirection;
      const type = selectedType === 'all' ? undefined : selectedType;
      const instId = symbolFilter.trim() || undefined;

      const response = await adminService.getHistoricalPositions(page, pagination.pageSize, instId, direction, type);

      setRecords(response.records);
      setPagination(response.pagination);
      setSummary(response.summary);
    } catch (error: any) {
      console.error('获取历史持仓失败:', error);
      showToast(error.message || '获取历史持仓失败', 'error');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const data = await accountService.getPositionStatistics(profitTimeRange);
      setStats(data);
    } catch (error) {
      console.error('获取统计失败:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // 初始加载
  useEffect(() => {
    // 防止重复初始化
    if (hasLoadedRef.current) {
      return;
    }
    hasLoadedRef.current = true;
    fetchRecords(1);
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 当时间范围变化时重新获取统计
  useEffect(() => {
    if (hasLoadedRef.current) {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profitTimeRange]);

  // 筛选条件变化时的防抖处理（不包括 symbolFilter，它需要按回车键查询）
  useEffect(() => {
    // 清除之前的定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 如果已经初始化过，才执行防抖加载
    if (hasLoadedRef.current) {
      // 设置防抖定时器（500ms）
      debounceTimerRef.current = setTimeout(() => {
        fetchRecords(1);
      }, 500);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDirection, selectedType]);

  const handleRefresh = () => {
    fetchRecords(pagination.page);
  };

  const handlePageChange = (newPage: number) => {
    if (pagination && (newPage < 1 || newPage > pagination.totalPages)) {
      return;
    }
    fetchRecords(newPage);
  };

  const handleSymbolSearch = () => {
    fetchRecords(1);
  };

  return (
    <GradientBackground>
      <div className="min-h-screen">
        <ParticleBackground />

        {/* 页面头部 */}
        <PageHeader title="历史持仓" onBack={() => setCurrentPage('personal-center')} />

        {/* 筛选栏 */}
        <FilterBar
          symbolFilter={symbolFilter}
          onSymbolFilterChange={setSymbolFilter}
          onSymbolSearch={handleSymbolSearch}
          selectedDirection={selectedDirection}
          onDirectionChange={setSelectedDirection}
          selectedType={selectedType}
          onTypeChange={setSelectedType}
        />

        {/* 内容区域 */}
        <div className="px-4 py-4" style={{ marginTop: '120px', paddingBottom: pagination ? '80px' : '20px' }}>
          {/* 汇总统计 */}
          {(stats || statsLoading) && (
            <SummaryStatsModule
              stats={stats!}
              loading={statsLoading}
              timeRange={profitTimeRange}
              onTimeRangeChange={setProfitTimeRange}
            />
          )}

          {/* 统计摘要 */}
          {summary && <SummaryCard summary={summary} />}

          {/* 记录列表 */}
          <PositionList loading={loading} records={records} onRefresh={handleRefresh} />
        </div>

        {/* 分页组件 */}
        {pagination && <PaginationBar pagination={pagination} loading={loading} onPageChange={handlePageChange} />}
      </div>
    </GradientBackground>
  );
};

export default HistoricalPositions;
